// src/components/clinica/Odontograma.js
// =========================================================================
// 🦷 ODONTOGRAMA (notación FDI)
// Se elige un estado en la paleta y se pulsa sobre las piezas para marcarlo.
// Solo se guardan las piezas que NO están sanas.
// =========================================================================
import React, { useState } from 'react';
import { useClinica } from '../../context/ClinicaContext';

const CUADRANTES = [
    ['18', '17', '16', '15', '14', '13', '12', '11'], ['21', '22', '23', '24', '25', '26', '27', '28'],
    ['48', '47', '46', '45', '44', '43', '42', '41'], ['31', '32', '33', '34', '35', '36', '37', '38']
];

export default function Odontograma({ valor = {}, onChange, disabled }) {
    const { odontogramaCatalogo } = useClinica();
    const estados = odontogramaCatalogo?.estados || [{ valor: 'sano', etiqueta: 'Sano', color: '#e2e8f0' }];
    const porValor = Object.fromEntries(estados.map(e => [e.valor, e]));
    const [pincel, setPincel] = useState(estados.find(e => e.valor === 'caries')?.valor || estados[0].valor);

    const marcar = (pieza) => {
        if (disabled) return;
        const siguiente = { ...valor };
        if (pincel === 'sano' || siguiente[pieza] === pincel) delete siguiente[pieza];
        else siguiente[pieza] = pincel;
        onChange(siguiente);
    };

    // Función (no componente): así las piezas no se vuelven a montar en cada clic
    const dibujarPieza = (pieza) => {
        const estado = porValor[valor[pieza] || 'sano'] || porValor.sano;
        const oscuro = ['ausente', 'caries', 'endodoncia', 'obturado', 'implante'].includes(estado?.valor);
        return (
            <button
                key={pieza}
                type="button"
                onClick={() => marcar(pieza)}
                disabled={disabled}
                title={`Pieza ${pieza}: ${estado?.etiqueta || 'Sano'}`}
                aria-label={`Pieza ${pieza}: ${estado?.etiqueta || 'Sano'}`}
                className="w-8 h-10 sm:w-9 sm:h-11 rounded-lg border border-slate-300 text-[11px] font-black transition-transform hover:scale-110 disabled:hover:scale-100 disabled:cursor-not-allowed"
                style={{ backgroundColor: estado?.color, color: oscuro ? '#fff' : '#334155' }}
            >
                {pieza}
            </button>
        );
    };

    return (
        <div className="space-y-4">
            {!disabled && (
                <div className="flex flex-wrap gap-2">
                    {estados.map(e => (
                        <button
                            key={e.valor}
                            type="button"
                            onClick={() => setPincel(e.valor)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${pincel === e.valor ? 'border-slate-900 ring-2 ring-slate-900/10 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500'}`}
                        >
                            <span className="w-3 h-3 rounded-full border border-slate-300" style={{ backgroundColor: e.color }} />
                            {e.etiqueta}
                        </button>
                    ))}
                </div>
            )}
            <div className="overflow-x-auto">
                <div className="inline-flex flex-col gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                    {[0, 2].map(fila => (
                        <div key={fila} className="flex gap-4">
                            <div className="flex gap-1">{CUADRANTES[fila].map(dibujarPieza)}</div>
                            <div className="w-px bg-slate-300" />
                            <div className="flex gap-1">{CUADRANTES[fila + 1].map(dibujarPieza)}</div>
                        </div>
                    ))}
                </div>
            </div>
            <p className="text-xs font-semibold text-slate-400">
                {disabled ? 'Odontograma registrado en esta visita.' : 'Elige un estado y pulsa sobre las piezas. Pulsa de nuevo para volver a «Sano».'}
            </p>
        </div>
    );
}
